/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";
import {
  getSlashReasonDecoder,
  getSlashReasonEncoder,
  type SlashReason,
  type SlashReasonArgs,
} from "../types";

export const SLASH_STAKE_DISCRIMINATOR = new Uint8Array([
  190, 242, 137, 27, 41, 18, 233, 37,
]);

export function getSlashStakeDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(SLASH_STAKE_DISCRIMINATOR);
}

export type SlashStakeInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountStakingAccount extends string | AccountMeta<string> = string,
  TAccountStakingConfig extends string | AccountMeta<string> = string,
  TAccountStakingVault extends string | AccountMeta<string> = string,
  TAccountTreasury extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountTokenProgram extends string | AccountMeta<string> =
    "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountStakingAccount extends string
        ? WritableAccount<TAccountStakingAccount>
        : TAccountStakingAccount,
      TAccountStakingConfig extends string
        ? ReadonlyAccount<TAccountStakingConfig>
        : TAccountStakingConfig,
      TAccountStakingVault extends string
        ? WritableAccount<TAccountStakingVault>
        : TAccountStakingVault,
      TAccountTreasury extends string
        ? WritableAccount<TAccountTreasury>
        : TAccountTreasury,
      TAccountAuthority extends string
        ? ReadonlySignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountTokenProgram extends string
        ? ReadonlyAccount<TAccountTokenProgram>
        : TAccountTokenProgram,
      ...TRemainingAccounts,
    ]
  >;

export type SlashStakeInstructionData = {
  discriminator: ReadonlyUint8Array;
  owner: Address;
  reason: SlashReason;
  customAmount: Option<bigint>;
};

export type SlashStakeInstructionDataArgs = {
  owner: Address;
  reason: SlashReasonArgs;
  customAmount: OptionOrNullable<number | bigint>;
};

export function getSlashStakeInstructionDataEncoder(): Encoder<SlashStakeInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["owner", getAddressEncoder()],
      ["reason", getSlashReasonEncoder()],
      ["customAmount", getOptionEncoder(getU64Encoder())],
    ]),
    (value) => ({ ...value, discriminator: SLASH_STAKE_DISCRIMINATOR }),
  );
}

export function getSlashStakeInstructionDataDecoder(): Decoder<SlashStakeInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["owner", getAddressDecoder()],
    ["reason", getSlashReasonDecoder()],
    ["customAmount", getOptionDecoder(getU64Decoder())],
  ]);
}

export function getSlashStakeInstructionDataCodec(): Codec<
  SlashStakeInstructionDataArgs,
  SlashStakeInstructionData
> {
  return combineCodec(
    getSlashStakeInstructionDataEncoder(),
    getSlashStakeInstructionDataDecoder(),
  );
}

export type SlashStakeAsyncInput<
  TAccountStakingAccount extends string = string,
  TAccountStakingConfig extends string = string,
  TAccountStakingVault extends string = string,
  TAccountTreasury extends string = string,
  TAccountAuthority extends string = string,
  TAccountTokenProgram extends string = string,
> = {
  stakingAccount?: Address<TAccountStakingAccount>;
  stakingConfig?: Address<TAccountStakingConfig>;
  stakingVault?: Address<TAccountStakingVault>;
  treasury: Address<TAccountTreasury>;
  authority: TransactionSigner<TAccountAuthority>;
  tokenProgram?: Address<TAccountTokenProgram>;
  owner: SlashStakeInstructionDataArgs["owner"];
  reason: SlashStakeInstructionDataArgs["reason"];
  customAmount: SlashStakeInstructionDataArgs["customAmount"];
};

export async function getSlashStakeInstructionAsync<
  TAccountStakingAccount extends string,
  TAccountStakingConfig extends string,
  TAccountStakingVault extends string,
  TAccountTreasury extends string,
  TAccountAuthority extends string,
  TAccountTokenProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: SlashStakeAsyncInput<
    TAccountStakingAccount,
    TAccountStakingConfig,
    TAccountStakingVault,
    TAccountTreasury,
    TAccountAuthority,
    TAccountTokenProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  SlashStakeInstruction<
    TProgramAddress,
    TAccountStakingAccount,
    TAccountStakingConfig,
    TAccountStakingVault,
    TAccountTreasury,
    TAccountAuthority,
    TAccountTokenProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    stakingAccount: { value: input.stakingAccount ?? null, isWritable: true },
    stakingConfig: { value: input.stakingConfig ?? null, isWritable: false },
    stakingVault: { value: input.stakingVault ?? null, isWritable: true },
    treasury: { value: input.treasury ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.stakingAccount.value) {
    accounts.stakingAccount.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([115, 116, 97, 107, 105, 110, 103]),
        ),
        getAddressEncoder().encode(expectSome(args.owner)),
      ],
    });
  }
  if (!accounts.stakingConfig.value) {
    accounts.stakingConfig.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            115, 116, 97, 107, 105, 110, 103, 95, 99, 111, 110, 102, 105, 103,
          ]),
        ),
      ],
    });
  }
  if (!accounts.stakingVault.value) {
    accounts.stakingVault.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            115, 116, 97, 107, 105, 110, 103, 95, 118, 97, 117, 108, 116,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.stakingConfig.value)),
      ],
    });
  }
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.stakingAccount),
      getAccountMeta(accounts.stakingConfig),
      getAccountMeta(accounts.stakingVault),
      getAccountMeta(accounts.treasury),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.tokenProgram),
    ],
    data: getSlashStakeInstructionDataEncoder().encode(
      args as SlashStakeInstructionDataArgs,
    ),
    programAddress,
  } as SlashStakeInstruction<
    TProgramAddress,
    TAccountStakingAccount,
    TAccountStakingConfig,
    TAccountStakingVault,
    TAccountTreasury,
    TAccountAuthority,
    TAccountTokenProgram
  >);
}

export type SlashStakeInput<
  TAccountStakingAccount extends string = string,
  TAccountStakingConfig extends string = string,
  TAccountStakingVault extends string = string,
  TAccountTreasury extends string = string,
  TAccountAuthority extends string = string,
  TAccountTokenProgram extends string = string,
> = {
  stakingAccount: Address<TAccountStakingAccount>;
  stakingConfig: Address<TAccountStakingConfig>;
  stakingVault: Address<TAccountStakingVault>;
  treasury: Address<TAccountTreasury>;
  authority: TransactionSigner<TAccountAuthority>;
  tokenProgram?: Address<TAccountTokenProgram>;
  owner: SlashStakeInstructionDataArgs["owner"];
  reason: SlashStakeInstructionDataArgs["reason"];
  customAmount: SlashStakeInstructionDataArgs["customAmount"];
};

export function getSlashStakeInstruction<
  TAccountStakingAccount extends string,
  TAccountStakingConfig extends string,
  TAccountStakingVault extends string,
  TAccountTreasury extends string,
  TAccountAuthority extends string,
  TAccountTokenProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: SlashStakeInput<
    TAccountStakingAccount,
    TAccountStakingConfig,
    TAccountStakingVault,
    TAccountTreasury,
    TAccountAuthority,
    TAccountTokenProgram
  >,
  config?: { programAddress?: TProgramAddress },
): SlashStakeInstruction<
  TProgramAddress,
  TAccountStakingAccount,
  TAccountStakingConfig,
  TAccountStakingVault,
  TAccountTreasury,
  TAccountAuthority,
  TAccountTokenProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    stakingAccount: { value: input.stakingAccount ?? null, isWritable: true },
    stakingConfig: { value: input.stakingConfig ?? null, isWritable: false },
    stakingVault: { value: input.stakingVault ?? null, isWritable: true },
    treasury: { value: input.treasury ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: false },
    tokenProgram: { value: input.tokenProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.tokenProgram.value) {
    accounts.tokenProgram.value =
      "TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA" as Address<"TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.stakingAccount),
      getAccountMeta(accounts.stakingConfig),
      getAccountMeta(accounts.stakingVault),
      getAccountMeta(accounts.treasury),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.tokenProgram),
    ],
    data: getSlashStakeInstructionDataEncoder().encode(
      args as SlashStakeInstructionDataArgs,
    ),
    programAddress,
  } as SlashStakeInstruction<
    TProgramAddress,
    TAccountStakingAccount,
    TAccountStakingConfig,
    TAccountStakingVault,
    TAccountTreasury,
    TAccountAuthority,
    TAccountTokenProgram
  >);
}

export type ParsedSlashStakeInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    stakingAccount: TAccountMetas[0];
    stakingConfig: TAccountMetas[1];
    stakingVault: TAccountMetas[2];
    treasury: TAccountMetas[3];
    authority: TAccountMetas[4];
    tokenProgram: TAccountMetas[5];
  };
  data: SlashStakeInstructionData;
};

export function parseSlashStakeInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSlashStakeInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 6) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      stakingAccount: getNextAccount(),
      stakingConfig: getNextAccount(),
      stakingVault: getNextAccount(),
      treasury: getNextAccount(),
      authority: getNextAccount(),
      tokenProgram: getNextAccount(),
    },
    data: getSlashStakeInstructionDataDecoder().decode(instruction.data),
  };
}
